import { getOffersPublishedByMonthData } from "Components/Stats/Data/Offres/Total";
import Margin from "Components/Stats/Margin";
import { fetchGet } from "Services/api";
import { isNotEmptyArray } from "Services/functions";
import { DateTime } from "luxon";
import { Calendar } from "primereact/calendar";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import NivoBar from "Components/Stats/Charts/NivoBar";

/**
 * Composant 'OffersByMonth'.
 *
 * Ce composant affiche un graphique du nombre total d'offres publiées par mois dans une université,
 * sur une période donnée. Les données sont récupérées à partir de l'API et formatées pour le graphique
 * à l'aide de fonctions spécifiques. Les dates de début et de fin de la période choisie sont gérées via
 * l'état local du composant avec le hook useState.
 *
 * - {@link getOffersPublishedByMonthData} : Fonction qui formate les données pour le graphique.
 *
 * - {@link NivoBar} : Composant générique de graphique en barres implémenté avec la librairie Nivo.
 *
 *
 * @param {Object} props - Les propriétés du composant.
 * @param {Object} props.auth - Les informations d'authentification de l'utilisateur. (depuis Redux)
 * @param {Object} props.secondaryTables - Les données des tables secondaires. (depuis Redux)
 * @param {Object} props.states - Les états des offres. (depuis Redux)
 * @param {string} props.universityName - Le nom de l'université pour laquelle afficher les statistiques.
 * @returns {JSX.Element} Le graphique du nombre total d'offres publiées par mois.
 *
 *
 * @example
 * // Utilisation du composant 'OffersByMonth' dans un autre composant.
 * import OffersByMonth from './OffersByMonth';
 * // ...
 * <OffersByMonth
 *   universityName="Nom de l'université"
 * />
 */
const OffersByMonth = (props) => {
  // Variables contenant les dates de début et de fin de la période choisie
  const [offersByMonthPeriod, setOffersByMonthPeriod] = useState([
    DateTime.local()
      .startOf("month")
      .minus({ months: 6 })
      .plus({ days: 1 })
      .toJSDate(),
    DateTime.local().endOf("month").minus({ months: 1 }).toJSDate(),
  ]);

  const [offersByMonth, setOffersByMonth] = useState([]);

  /**
   * Mise à jour des données pour les offres par mois (publiées sur une période)
   */
  async function updateOffersByMonthPeriod() {
    let requestData = await fetchGet(
      "/offers/stats/count/category",
      props.auth.token,
      {
        // Filtre période, posté après la date de début et avant la date de fin
        "postedAt[after]": offersByMonthPeriod[0].toISOString().slice(0, 10),
        "postedAt[before]": offersByMonthPeriod[1].toISOString().slice(0, 10),
        // Filtre état: publié / en attente
        "state[]": [
          props.states.statesData.find(
            (state) => state.statusLabel === "Publié"
          ).id,
          //   props.states.statesData.find(
          //     (state) => state.statusLabel === "En Attente"
          //   ).id,
        ],
      }
    );
    setOffersByMonth(
      getOffersPublishedByMonthData(
        requestData,
        offersByMonthPeriod[0],
        offersByMonthPeriod[1]
      )
    );
  }

  useEffect(() => {
    // Si les données API sont chargées et que les données de catégories sont chargées
    // Et que la période est définie, et correcte
    if (
      isNotEmptyArray(props.secondaryTables.offerCategoriesData) &&
      offersByMonthPeriod[0] instanceof Date &&
      !isNaN(offersByMonthPeriod[0]) &&
      offersByMonthPeriod[1] instanceof Date &&
      !isNaN(offersByMonthPeriod[1])
    )
      updateOffersByMonthPeriod();
  }, [offersByMonthPeriod, props.secondaryTables.offerCategoriesData]);

  return (
    <>
      {/* Graphique des offres par mois (sur une période donnée) */}
      <React.Fragment>
        <div className="calendar">
          <label>Période choisie</label>
          <Calendar
            value={offersByMonthPeriod}
            onChange={(e) => {
              if (e.value[0] && e.value[1]) {
                let beforeDate = DateTime.fromJSDate(e.value[0]);
                let afterDate = DateTime.fromJSDate(e.value[1]).plus({
                  months: 1,
                });
                setOffersByMonthPeriod([
                  beforeDate.toJSDate(),
                  afterDate.toJSDate(),
                ]);
              } else {
                setOffersByMonthPeriod(e.value);
              }
            }}
            showIcon
            view="month"
            dateFormat="mm/yy"
            selectionMode="range"
            maxDate={DateTime.local().minus({ months: 1 }).toJSDate()}
          />
        </div>
      </React.Fragment>
      <Margin position="bottom" size="s" />
      <NivoBar
        data={offersByMonth}
        keysNames={["Offres"]}
        indexName="date"
        title={
          "Nombre total d'offres publiées par mois dans l'université " +
          props.universityName
        }
      />
      <Margin position="bottom" size="" />
    </>
  );
};

OffersByMonth.propTypes = {
  auth: PropTypes.object.isRequired,
  secondaryTables: PropTypes.object.isRequired,
  states: PropTypes.object.isRequired,
  universityName: PropTypes.string.isRequired,
};

const mapStateToProps = (state) => ({
  states: state.states,
  auth: state.auth,
  secondaryTables: state.secondaryTables,
});

export default connect(mapStateToProps)(OffersByMonth);
